import { useMemo, useState } from 'react'
import { TransitionLoader } from '@opallabs/kuiper'
import { useOnce } from '@opallabs/lambda'
import { APPROVAL_GLYPH } from '@opallabs/lapidary'
import type { WithTheme } from '@opallabs/lapidary'
import { ApprovalItem } from '@opallabs/satellite'
import { AssignmentStatus } from '@opallabs/satellite/src/AssignmentHelpers/types'
import { NormalizedApproval } from '@opallabs/wormhole/types'
import { CollapsibleList } from '../../CollapsibleList'
import { EmptyState } from '../../Shared/EmptyState'
import * as sharedStyles from '../../Shared/styles'
import { Runnable } from '../../types'

export type PendingPanelProps = WithTheme & {
  createOverdueCheckpoint: Runnable
  createPendingCheckpoint: Runnable
  getOverdueApprovals: Runnable
  getPendingApprovals: Runnable
  isLoading: boolean
  overdueApprovals: NormalizedApproval[]
  pendingApprovals: NormalizedApproval[]
  setApprovalPreviewCard: (approval: NormalizedApproval | null) => void
}

export const PendingPanel: React.FC<PendingPanelProps> = ({
  createOverdueCheckpoint,
  createPendingCheckpoint,
  getOverdueApprovals,
  getPendingApprovals,
  isLoading,
  overdueApprovals,
  pendingApprovals,
  setApprovalPreviewCard,
  theme = 'light',
}) => {
  const [activeApproval, setActiveApproval] = useState<string>(
    pendingApprovals.length ? pendingApprovals[0].id : ''
  )

  const isEmpty = useMemo(
    () => pendingApprovals.length === 0 && overdueApprovals.length === 0,
    [pendingApprovals, overdueApprovals]
  )

  useOnce(() => {
    getOverdueApprovals()
    getPendingApprovals()
  })

  if (!isLoading && isEmpty) {
    return (
      <EmptyState
        icon={APPROVAL_GLYPH}
        text="You don’t have any pending or overdue approvals right now"
      />
    )
  }

  return (
    <TransitionLoader
      loaded={!isLoading}
      minHeight={600} // this is an arbitrary size that looks good
      theme="transparent"
    >
      <div>
        <CollapsibleList
          defaultExpanded={true}
          onExpand={createOverdueCheckpoint}
          styleOverrides={{
            base: sharedStyles.collapsibleList,
            titleRow: sharedStyles.collapsibleListTitle({ theme }),
          }}
          warning={true}
          title="My Overdue Approvals"
          iconName="gem-warning"
          textRender={(count: number): string =>
            `You have ${count} overdue approval${count !== 1 ? 's' : ''}`
          }
        >
          {overdueApprovals.map((approval) => (
            <ApprovalItem
              {...approval}
              assignmentStatus={
                activeApproval === approval.id
                  ? AssignmentStatus.Active
                  : approval.assignmentStatus
              }
              key={`approval-overdue-${approval.id}`}
              onItemClick={(): void => {
                setActiveApproval(approval.id)
                setApprovalPreviewCard(approval)
              }}
              overdue={true}
            />
          ))}
        </CollapsibleList>
        <CollapsibleList
          defaultExpanded={true}
          onExpand={createPendingCheckpoint}
          styleOverrides={{
            base: sharedStyles.collapsibleList,
            titleRow: sharedStyles.collapsibleListTitle({ theme }),
          }}
          title="My Pending Approvals"
          iconName={APPROVAL_GLYPH}
          textRender={(count: number): string =>
            `You have ${count} pending approval${count !== 1 ? 's' : ''}`
          }
        >
          {pendingApprovals.map((approval) => (
            <ApprovalItem
              {...approval}
              assignmentStatus={
                activeApproval === approval.id
                  ? AssignmentStatus.Active
                  : approval.assignmentStatus
              }
              key={`approval-pending-${approval.id}`}
              onItemClick={(): void => {
                setActiveApproval(approval.id)
                setApprovalPreviewCard(approval)
              }}
            />
          ))}
        </CollapsibleList>
      </div>
    </TransitionLoader>
  )
}
